import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import { invalidateBalanceViews } from '@/billing/services/invalidate-balance-views';
import type {
  AppointmentResponse,
  AppointmentsDetailsResponse,
  DeviceUpdateRequest,
  PreLoadAppointmentResponse,
} from '@/types/scheduling-api';
import { APPOINTMENT_LIST_QUERIES } from './appointment-list-keys';
import { RESOURCE_AVAILABILITY_KEY } from './use-device-availability';

/**
 * `PUT /api/Appointment/Resource/{id}` — the DEVICE's own edit route.
 *
 * PROD-2526. The device edit rail used to save through `useUpdateAppointment`,
 * the PROVIDER hook (`PUT /api/Appointment/{id}`), and a device booking could
 * not be rescheduled at all: on dev that route answered 500 for every call.
 * Scheduling has had the device route since PROD-2385 (#1984, series edits
 * since PROD-2383 #1995), and since PROD-2526 the provider route REFUSES a
 * device appointment (`DeviceUpdateOnProviderRoute`, BR-SCHED-037). Andrew's
 * standing ruling (2026-09-13) is that the device APIs mirror the provider ones
 * and never overload the provider route. So this is the device twin of
 * `useUpdateAppointment`, beside it rather than a device flag inside it.
 *
 * Mirrors `useUpdateAppointment` on purpose, so the two behave identically from
 * the cache's point of view:
 *   - onMutate patches the PreLoad cache and every appointment list in place
 *     (start / end / comment / minutes), so the tile moves the moment Save is
 *     pressed, and snapshots both;
 *   - onError restores the snapshots, so a refused move snaps back;
 *   - onSettled invalidates the lists, the PreLoad, the invoice detail and the
 *     balance views, as the provider hook does, PLUS the device availability
 *     queries: moving a booking frees one window and takes another.
 *
 * One deliberate difference: the global error toast is opted out
 * (`skipGlobalError`), as `useBookDevice` does. The callers map the refusal
 * themselves with `classifyDeviceBookingFailure`, which knows the device codes
 * (`ResourceBooked`, `SlotBookedFordate`, `DeviceImmutable`, …), and a generic
 * "Something went wrong" on top of that would say the same thing twice.
 */
export const DEVICE_UPDATE_URL = (appointmentId: string) =>
  `/api/Appointment/Resource/${appointmentId}`;

interface UpdateDeviceAppointmentVariables {
  appointmentId: string;
  body: DeviceUpdateRequest;
}

interface OptimisticContext {
  snapshots: { key: readonly unknown[]; data: unknown }[];
}

/** The fields a device edit can change that a calendar tile or the popup shows. */
function visiblePatch(body: DeviceUpdateRequest): Partial<AppointmentResponse> {
  const patch: Record<string, unknown> = {};
  if (body.start) patch.start = body.start;
  if (body.end) patch.end = body.end;
  if (body.minutesDuration != null) patch.minutesDuration = body.minutesDuration;
  if (body.comment !== undefined) patch.comment = body.comment;
  return patch as Partial<AppointmentResponse>;
}

export function useUpdateDeviceAppointment() {
  const queryClient = useQueryClient();

  return useMutation<string, Error, UpdateDeviceAppointmentVariables, OptimisticContext>({
    mutationKey: ['update-device-appointment'],
    mutationFn: ({ appointmentId, body }) => put<string>(DEVICE_UPDATE_URL(appointmentId), body),
    meta: { skipGlobalError: true },

    onMutate: ({ appointmentId, body }) => {
      const snapshots: OptimisticContext['snapshots'] = [];
      const patch = visiblePatch(body);

      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['appointment-details'] })
        .forEach((q) => snapshots.push({ key: q.queryKey, data: q.state.data }));
      queryClient.setQueriesData<PreLoadAppointmentResponse | undefined>(
        { queryKey: ['appointment-details'] },
        (prev) => {
          if (!prev?.appointment || prev.appointment.id !== appointmentId) return prev;
          return { ...prev, appointment: { ...prev.appointment, ...patch } as AppointmentResponse };
        },
      );

      queryClient
        .getQueryCache()
        .findAll(APPOINTMENT_LIST_QUERIES)
        .forEach((q) => snapshots.push({ key: q.queryKey, data: q.state.data }));
      queryClient.setQueriesData<AppointmentsDetailsResponse | undefined>(
        APPOINTMENT_LIST_QUERIES,
        (prev) => {
          if (!prev?.appointments) return prev;
          const idx = prev.appointments.findIndex((a) => a.id === appointmentId);
          if (idx < 0) return prev;
          const next = prev.appointments.slice();
          next[idx] = { ...next[idx], ...(patch as Record<string, unknown>) } as (typeof next)[number];
          return { ...prev, appointments: next };
        },
      );

      return { snapshots };
    },

    onError: (_err, _vars, ctx) => {
      ctx?.snapshots.forEach(({ key, data }) => queryClient.setQueryData(key, data));
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['appointment-details'] });
      queryClient.invalidateQueries(APPOINTMENT_LIST_QUERIES);
      queryClient.invalidateQueries({ queryKey: [RESOURCE_AVAILABILITY_KEY] });
      // The device slot feed the booking rail reads (PROD-2455).
      queryClient.invalidateQueries({ queryKey: ['device-calendar-slots'] });
      // A series edit moves sessions the list shows under the view panel's Appointments tab.
      queryClient.invalidateQueries({ queryKey: ['recurring-appointment-list'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', 'detail'] });
      invalidateBalanceViews(queryClient);
    },
  });
}
